import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./EncorePaywall.module.scss"

// ---------------------------------------------------------------------------
// "Encore" — the pitch is NOT privacy. It's VIDEO / motion memories.
//
// Free plans quietly transcode your videos and Live Photos into blurry,
// stuttering, low-frame-rate copies. Premium keeps every clip exactly as it
// moved — 4K, HDR, slow-mo, the little Live-Photo wiggle that a still can never
// hold. None of the prior 18 paywalls pitched motion/video.
//
// The screen is a working MEDIA PLAYER, not a hero + card: a light
// "screening room" wall with a single dark screen that plays (Ken-Burns +
// timecode + a real draggable scrubber), a Free↔Premium quality toggle that
// visibly degrades / sharpens the picture, and a horizontal "Up next" queue you
// tap to load. Warm, bright, joyful art direction. Photos are pre-fetched into
// public/encore so it renders fully offline with the debug data.
// ---------------------------------------------------------------------------

const PUB = process.env.PUBLIC_URL ?? ""
const src = (file: string) => `${PUB}/encore/${file}.jpg`

// Each clip "plays" over this fixed wall-clock time regardless of its shown
// duration, so the demo stays snappy (a 1:03 clip still scrubs 0:00→1:03).
const PLAYTHROUGH_MS = 6200

type Clip = {
  img: string
  title: string
  place: string
  dur: number // seconds — drives the timecode + scrubber labels
  kind: "LIVE PHOTO" | "SLO-MO 240" | "4K · HDR" | "4K · 60"
}

const CLIPS: Clip[] = [
  {
    img: "sprinkler",
    title: "Last day of camp",
    place: "Backyard · July",
    dur: 14,
    kind: "SLO-MO 240",
  },
  {
    img: "dance",
    title: "Her first dance",
    place: "The wedding",
    dur: 52,
    kind: "LIVE PHOTO",
  },
  {
    img: "wave",
    title: "The set rolls in",
    place: "Big Sur",
    dur: 63,
    kind: "4K · HDR",
  },
  {
    img: "candles",
    title: "She’s five",
    place: "Kitchen · 7:41pm",
    dur: 9,
    kind: "LIVE PHOTO",
  },
  {
    img: "dog",
    title: "Full sprint",
    place: "Ocean Beach",
    dur: 22,
    kind: "SLO-MO 240",
  },
  {
    img: "concert",
    title: "Front row, finally",
    place: "The encore",
    dur: 71,
    kind: "4K · 60",
  },
  {
    img: "skate",
    title: "Landed it",
    place: "The ledge",
    dur: 18,
    kind: "SLO-MO 240",
  },
  {
    img: "confetti",
    title: "Midnight",
    place: "New Year’s",
    dur: 11,
    kind: "LIVE PHOTO",
  },
]

function fmtTime(sec: number) {
  const s = Math.max(0, Math.floor(sec))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
}

function EncorePaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""
  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])

  // ── player state ─────────────────────────────────────────────────────────
  const [active, setActive] = useState(0)
  const [playing, setPlaying] = useState(!reduce)
  const [progress, setProgress] = useState(reduce ? 0.34 : 0)
  const [quality, setQuality] = useState<"premium" | "free">("premium")
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  const progressRef = useRef(reduce ? 0.34 : 0)
  const draggingRef = useRef(false)
  const clip = CLIPS[active]

  const goTo = useCallback((i: number) => {
    setActive(((i % CLIPS.length) + CLIPS.length) % CLIPS.length)
    progressRef.current = 0
    setProgress(0)
    setPlaying(true)
  }, [])

  // rAF playback loop — advances the scrubber, auto-cuts to the next clip.
  useEffect(() => {
    if (!playing || reduce) return
    let raf = 0
    let last: number | null = null
    const tick = (ts: number) => {
      if (last == null) last = ts
      const dt = ts - last
      last = ts
      if (!draggingRef.current) {
        let np = progressRef.current + dt / PLAYTHROUGH_MS
        if (np >= 1) {
          np = 0
          setActive((a) => (a + 1) % CLIPS.length)
        }
        progressRef.current = np
        setProgress(np)
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [playing, reduce])

  // ── scrubber drag ──────────────────────────────────────────────────────────
  const trackRef = useRef<HTMLDivElement>(null)
  const scrubTo = useCallback((clientX: number) => {
    const el = trackRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (clientX - r.left) / r.width))
    progressRef.current = ratio
    setProgress(ratio)
  }, [])
  const onTrackDown = (e: React.PointerEvent) => {
    draggingRef.current = true
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    scrubTo(e.clientX)
  }
  const onTrackMove = (e: React.PointerEvent) => {
    if (draggingRef.current) scrubTo(e.clientX)
  }
  const onTrackUp = () => {
    draggingRef.current = false
  }

  const handleClose = () => native.postNativeEvent("close")
  const buy = () => {
    const p = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (p) native.subscribe(p)
  }

  const kenClass = `${styles.frame} ${styles["ken" + (active % 4)]}`

  return (
    <div className={styles.page}>
      {/* header — a tiny console masthead, close is immediate (not delayed) */}
      <header className={styles.top}>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <span className={styles.brand}>
          <span className={styles.dot} /> CloudVault Premium
        </span>
        <span className={styles.nowPlaying}>NOW&nbsp;PLAYING</span>
      </header>

      {/* the screen ------------------------------------------------------------ */}
      <div className={styles.stage}>
        <div
          className={`${styles.screen} ${quality === "free" ? styles.degraded : ""} ${
            playing ? styles.isPlaying : ""
          }`}
        >
          <img
            key={active}
            className={kenClass}
            src={src(clip.img)}
            alt={clip.title}
            style={{
              animationPlayState: playing && !reduce ? "running" : "paused",
            }}
            draggable={false}
          />
          <div className={styles.grain} />
          {quality === "free" && <div className={styles.blocks} />}

          {/* overlays */}
          <div className={styles.badge}>
            {quality === "free" ? "480p" : clip.kind}
          </div>
          <div className={styles.tc}>
            {fmtTime(progress * clip.dur)} <span>/ {fmtTime(clip.dur)}</span>
          </div>

          {quality === "free" && (
            <div className={styles.buffer}>
              <span className={styles.spinner} /> buffering…
            </div>
          )}

          <button
            className={styles.playBtn}
            onClick={() => setPlaying((p) => !p)}
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? (
              <svg viewBox="0 0 24 24" width="26" height="26">
                <rect
                  x="6"
                  y="5"
                  width="4.2"
                  height="14"
                  rx="1"
                  fill="currentColor"
                />
                <rect
                  x="13.8"
                  y="5"
                  width="4.2"
                  height="14"
                  rx="1"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="26" height="26">
                <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
              </svg>
            )}
          </button>

          {/* caption strip */}
          <div className={styles.caption}>
            <div className={styles.capTitle}>{clip.title}</div>
            <div className={styles.capPlace}>{clip.place}</div>
          </div>

          {/* scrubber */}
          <div
            ref={trackRef}
            className={styles.track}
            onPointerDown={onTrackDown}
            onPointerMove={onTrackMove}
            onPointerUp={onTrackUp}
            onPointerCancel={onTrackUp}
          >
            <div className={styles.buffered} />
            <div
              className={styles.filled}
              style={{ width: `${progress * 100}%` }}
            />
            <div
              className={styles.head}
              style={{ left: `${progress * 100}%` }}
            />
          </div>
        </div>

        {/* quality toggle — the persuasion. Free visibly wrecks the picture. */}
        <div className={styles.quality}>
          <button
            className={quality === "free" ? styles.qOn : ""}
            onClick={() => setQuality("free")}
          >
            Free · 480p
          </button>
          <button
            className={quality === "premium" ? styles.qOn : ""}
            onClick={() => setQuality("premium")}
          >
            Premium · 4K
          </button>
        </div>
        <p className={styles.qNote}>
          {quality === "free"
            ? "This is what other apps do to your videos — recompressed to save their space."
            : "Every frame kept exactly as you shot it. Nothing recompressed."}
        </p>
      </div>

      {/* editorial line ------------------------------------------------------- */}
      <div className={styles.pitch}>
        <h1>
          Some moments <em>don’t hold still.</em>
        </h1>
        <p>
          Slow-mo, Live Photos, the 4K clips that only make sense in motion.
          Premium keeps them moving — full quality, on every device, forever.
        </p>
      </div>

      {/* up-next queue -------------------------------------------------------- */}
      <div className={styles.queueWrap}>
        <div className={styles.queueHead}>
          <span>Up next</span>
          <button className={styles.playAll} onClick={() => goTo(0)}>
            ▶ Play all
          </button>
        </div>
        <div className={styles.queue}>
          {CLIPS.map((c, i) => (
            <button
              key={c.img}
              className={`${styles.thumb} ${i === active ? styles.thumbOn : ""}`}
              onClick={() => goTo(i)}
            >
              <img src={src(c.img)} alt={c.title} draggable={false} />
              <span className={styles.dur}>{fmtTime(c.dur)}</span>
              {i === active && <span className={styles.nowTag}>▶</span>}
              <span className={styles.thumbTitle}>{c.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* offer — a "Season Pass" ticket -------------------------------------- */}
      <div className={styles.offer}>
        <div className={styles.ticketRow}>
          <button
            className={`${styles.ticket} ${plan === "yearly" ? styles.tOn : ""}`}
            onClick={() => setPlan("yearly")}
          >
            <div className={styles.tKick}>
              SEASON PASS
              {savings > 0 && (
                <span className={styles.save}>SAVE {savings}%</span>
              )}
            </div>
            <div className={styles.tPrice}>
              {yearlyPrice}
              <span className={styles.per}>/year</span>
            </div>
            <div className={styles.tSub}>
              12 months of everything, in motion · {yearlyPerMonth}/mo
            </div>
          </button>

          <button
            className={`${styles.ticket} ${styles.mini} ${
              plan === "monthly" ? styles.tOn : ""
            }`}
            onClick={() => setPlan("monthly")}
          >
            <div className={styles.tKick}>MONTHLY</div>
            <div className={styles.tPriceMini}>
              {monthlyPrice}
              <span className={styles.per}>/mo</span>
            </div>
            <div className={styles.tSub}>flexible, cancel anytime</div>
          </button>
        </div>

        <button className={styles.cta} onClick={buy}>
          <span className={styles.ctaTri} />
          {plan === "yearly"
            ? `Start Premium — ${yearlyPrice}/yr`
            : `Start Premium — ${monthlyPrice}/mo`}
        </button>

        <p className={styles.reassure}>
          Cancel anytime — you always keep access to your memories.
        </p>
        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default EncorePaywall
